export type CheckStatus = "fail" | "pass" | "warning";

export interface DiagnosticCheck {
  description: string;
  id: string;
  status: CheckStatus;
  title: string;
}

export type FileResultStatus = "done" | "loading" | "pending";

/**
 * Tag lifecycle for an analyzed file. Derived server-side from the score + user
 * actions (see `AUTO_CHECK_SCORE_THRESHOLD`):
 * - `pending_review`: AI score below the auto-check threshold. Blocks submission.
 * - `auto_checked`: AI score at or above the auto-check threshold. Cleared automatically.
 * - `overridden`: user manually accepted a below-threshold score.
 * - `failed`: AI processing failed.
 */
export type FileTag =
  | "auto_checked"
  | "failed"
  | "overridden"
  | "pending_review";

export interface FileResult {
  checks: DiagnosticCheck[];
  /** User-facing name, overriding `file.name` when the user has renamed the file. */
  displayName?: string;
  /** Server-side `file_evaluations.id`. `null` while we wait for the first AI verdict. */
  evaluationId: null | number;
  file: File;
  /** Stable React key. Maps to the server-side `attachments.id` (stringified). */
  id: string;
  /** Multipart transfer in progress (upload or replace). */
  isTransferring?: boolean;
  /** S3 thumbnail (`-thumbnail` suffix) for the strip. */
  previewUrl: null | string;
  /** Full S3 URL from the snapshot for the large preview. */
  remoteUrl: null | string;
  /** Score at the moment the tag was set. Useful for the overridden audit trail. */
  score: number;
  status: FileResultStatus;
  tag: FileTag;
  uploadProgress?: number;
}

/** Files are cleared for submission once every result is auto_checked or overridden. */
export function isFileResultCleared(result: FileResult): boolean {
  return (
    result.tag === "auto_checked" ||
    result.tag === "overridden" ||
    result.tag === "failed"
  );
}
